"use client";

import { useEffect, useRef, type ReactNode } from "react";

import { SNAPSHOT } from "./data";
import { mountFigures, mountRaster, mountTiles } from "./renderers";
import "./snapshot.css";

/**
 * Client shell for the Market Snapshot post.
 *
 * The prose is server-rendered markup passed in as children. This component owns
 * the three things that need the browser: the raster canvas, the shared tooltip,
 * and the drawings: six index thumbnails and six figures, all built imperatively
 * from SNAPSHOT once the markup is in the DOM. Everything is scoped under `.snap`.
 */
export function SnapshotShell({ children }: { children: ReactNode }) {
  const rootRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    const tip = tipRef.current;
    if (!root) return;
    mountTiles(root, SNAPSHOT);
    if (tip) mountFigures(root, SNAPSHOT, tip);
  }, []);

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    // Respect a user who has asked for less motion: draw the field once, no pointer tracking.
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      const teardown = mountRaster(cv);
      return teardown;
    }
    return mountRaster(cv);
  }, []);

  return (
    <div className="snap" ref={rootRef}>
      <div id="raster" className="snap-raster" aria-hidden="true">
        <canvas ref={canvasRef} />
      </div>
      <div className="tt" ref={tipRef} role="status" aria-live="off" />
      {children}
    </div>
  );
}
